import React, { useEffect, useRef } from 'react'
import { Animated, Easing } from 'react-native'
import Svg, { G, Path, SvgProps } from 'react-native-svg'

import useTheme from '@common/hooks/useTheme'

const SPIN_PATH_D =
  'M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z'

const PureSpinner: React.FC<SvgProps> = ({ width = 24, height = 24, color, style, ...rest }) => {
  const { theme } = useTheme()
  const spin = useRef(new Animated.Value(0)).current

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 750,
        easing: Easing.linear,
        // Native-driver loops are unreliable on web / extension (often stop after one iteration).
        useNativeDriver: false
      })
    )
    loop.start()
    return () => loop.stop()
  }, [spin])

  // Rotate the wrapping View, not the SVG <G>. The View is square (width === height) and the
  // artwork is centered in its viewBox, so the View's center pivot matches the artwork center
  // (no “orbit” wobble). Animating <G> via react-native-svg leaks invalid DOM attrs on web
  // (`collapsable`, `transform-origin`); a View transform is converted to clean CSS by RN Web.
  const rotation = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg']
  })

  const fill = color || theme.iconPrimary

  return (
    <Animated.View
      style={[
        { width: width as number, height: height as number, transform: [{ rotate: rotation }] },
        style
      ]}
    >
      <Svg width={width} height={height} viewBox="0 0 24 24" {...rest}>
        <G>
          <Path d={SPIN_PATH_D} fill={fill} />
        </G>
      </Svg>
    </Animated.View>
  )
}

export default React.memo(PureSpinner)
